/**
 * KPISelector Component
 * Multi-select for KPI metrics with drag-to-reorder capability
 */

import { useState } from 'react'
import { Check, GripVertical, ChevronDown, ChevronUp } from 'lucide-react'
import { AVAILABLE_KPIS, KPIDefinition } from '@/types/comparison'
import clsx from 'clsx'

interface KPISelectorProps {
  selectedKPIs: string[]
  onChange: (kpis: string[]) => void
  className?: string
}

export default function KPISelector({
  selectedKPIs,
  onChange,
  className
}: KPISelectorProps) {
  const [isExpanded, setIsExpanded] = useState(true)
  const [draggedItem, setDraggedItem] = useState<string | null>(null)

  const handleToggle = (kpiId: string) => {
    if (selectedKPIs.includes(kpiId)) {
      onChange(selectedKPIs.filter(id => id !== kpiId))
    } else {
      onChange([...selectedKPIs, kpiId])
    }
  }

  const handleSelectAll = () => {
    onChange(AVAILABLE_KPIS.map(kpi => kpi.id))
  }

  const handleClearAll = () => {
    onChange([])
  }

  const handleDragStart = (e: React.DragEvent, kpiId: string) => {
    setDraggedItem(kpiId)
    e.dataTransfer.effectAllowed = 'move'
  }

  const handleDragOver = (e: React.DragEvent, kpiId: string) => {
    e.preventDefault()
    if (draggedItem && draggedItem !== kpiId && selectedKPIs.includes(draggedItem)) {
      const currentIndex = selectedKPIs.indexOf(draggedItem)
      const targetIndex = selectedKPIs.indexOf(kpiId)

      if (currentIndex !== -1 && targetIndex !== -1) {
        const newOrder = [...selectedKPIs]
        newOrder.splice(currentIndex, 1)
        newOrder.splice(targetIndex, 0, draggedItem)
        onChange(newOrder)
      }
    }
  }

  const handleDragEnd = () => {
    setDraggedItem(null)
  }

  // Group KPIs by category
  const kpisByCategory = AVAILABLE_KPIS.reduce((acc, kpi) => {
    if (!acc[kpi.category]) {
      acc[kpi.category] = []
    }
    acc[kpi.category].push(kpi)
    return acc
  }, {} as Record<string, KPIDefinition[]>)

  const categoryLabels: Record<string, string> = {
    reliability: 'Reliability',
    performance: 'Performance',
    health: 'Health',
    anomaly: 'Anomaly Detection'
  }

  return (
    <div className={clsx('bg-surface-800 rounded-lg border border-surface-700', className)}>
      {/* Header */}
      <div
        className="flex items-center justify-between p-3 cursor-pointer hover:bg-surface-700/50"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-surface-200">KPI Metrics</h3>
          <span className="text-xs text-surface-400">
            ({selectedKPIs.length} selected)
          </span>
        </div>
        <div className="flex items-center gap-2">
          {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </div>
      </div>

      {isExpanded && (
        <>
          {/* Quick Actions */}
          <div className="px-3 pb-2 flex gap-2">
            <button
              onClick={handleSelectAll}
              className="text-xs text-primary-400 hover:text-primary-300"
            >
              Select All
            </button>
            <span className="text-surface-600">|</span>
            <button
              onClick={handleClearAll}
              className="text-xs text-surface-400 hover:text-surface-300"
            >
              Clear All
            </button>
          </div>

          {/* KPI List by Category */}
          <div className="max-h-72 overflow-y-auto border-t border-surface-700">
            {Object.entries(kpisByCategory).map(([category, kpis]) => (
              <div key={category} className="border-b border-surface-700/50 last:border-b-0">
                <div className="px-3 py-2 bg-surface-900/50">
                  <span className="text-xs font-semibold text-surface-400 uppercase">
                    {categoryLabels[category] || category}
                  </span>
                </div>
                <div className="divide-y divide-surface-700/30">
                  {kpis.map(kpi => {
                    const isSelected = selectedKPIs.includes(kpi.id)
                    const orderIndex = selectedKPIs.indexOf(kpi.id)

                    return (
                      <div
                        key={kpi.id}
                        draggable={isSelected}
                        onDragStart={(e) => handleDragStart(e, kpi.id)}
                        onDragOver={(e) => handleDragOver(e, kpi.id)}
                        onDragEnd={handleDragEnd}
                        onClick={() => handleToggle(kpi.id)}
                        className={clsx(
                          'flex items-center gap-2 px-3 py-2 cursor-pointer transition-colors',
                          'hover:bg-surface-700/50',
                          isSelected && 'bg-primary-500/10',
                          draggedItem === kpi.id && 'opacity-50'
                        )}
                      >
                        {/* Drag Handle (only for selected) */}
                        <div className={clsx(
                          'w-4 flex items-center justify-center',
                          isSelected ? 'cursor-grab' : 'invisible'
                        )}>
                          {isSelected && <GripVertical size={12} className="text-surface-500" />}
                        </div>

                        {/* Checkbox */}
                        <div className={clsx(
                          'w-4 h-4 rounded border flex items-center justify-center transition-colors',
                          isSelected
                            ? 'bg-primary-500 border-primary-500'
                            : 'border-surface-500 bg-surface-800'
                        )}>
                          {isSelected && <Check size={12} className="text-white" />}
                        </div>

                        {/* KPI Info */}
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-surface-200">{kpi.label}</span>
                            {kpi.unit && (
                              <span className="text-xs text-surface-500">({kpi.unit})</span>
                            )}
                          </div>
                          {kpi.description && (
                            <p className="text-xs text-surface-400">{kpi.description}</p>
                          )}
                        </div>

                        {/* Order Number (only for selected) */}
                        {isSelected && orderIndex !== -1 && (
                          <span className="w-5 h-5 flex items-center justify-center text-xs bg-primary-500/30 text-primary-300 rounded-full">
                            {orderIndex + 1}
                          </span>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Selected Order Preview */}
          {selectedKPIs.length > 0 && (
            <div className="p-2 border-t border-surface-700 bg-surface-900/50">
              <p className="text-xs text-surface-400 mb-1">Display order (drag to reorder):</p>
              <div className="flex flex-wrap gap-1">
                {selectedKPIs.map((kpiId, index) => {
                  const kpi = AVAILABLE_KPIS.find(k => k.id === kpiId)
                  return (
                    <span
                      key={kpiId}
                      className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-surface-700 text-surface-300 rounded"
                    >
                      <span className="text-primary-400">{index + 1}.</span>
                      {kpi?.label || kpiId}
                    </span>
                  )
                })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
